import { exist, Id } from '@campaign-portal/namespace/common/id';

declare global {
	interface Window {
		frameInVoice?: any;
		REPO?: any;
	}
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/* Users and Roles */
export interface TokenData {
	exp: number;
	jti: string;
	iat: number;
	iss: 'usersroles';
	sub: string;
	email: string;
	permissions: '';
}

export interface UserDetails {
	ID: Id<exist>;
	Login: string;
	Email: string;
	Details: {
		firstName: string;
		lastName: string;
		[key: string]: unknown;
	};

	[key: string]: unknown;
}

export interface User<ID = Id> {
	ID: ID;
	Login: string;
	Email: string;
	Details?: {
		firstName: string;
		lastName: string;
		[key: string]: unknown;
	};
	RolesIds: number[];
}

export interface Permission {
	Name: string;
	Fullname: string;
	Sub?: Permission[];
}

export interface Role<ID = Id> {
	ID: ID;
	Name: string;
	Author?: {
		Details: object;
		Email: string;
		ID: number;
		Login: string;
	};
	Permissions: string[];
}

export interface RoleDetails {
	ID: Id<exist>;
	Login: string;
	Email: string;
	Details: {
		firstName: string;
		lastName: string;
		[key: string]: unknown;
	};

	[key: string]: unknown;
}

export interface Author<ID = Id> {
	id: ID;
	details: {
		firstName: string;
		lastName: string;
	};
	email: string;
	login: string;
}

/* End of Users and Roles */
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/* HLR */
export type HlrInterface = 'http' | 'enum' | 'smsrt';

export interface EntityItem {
	key: string;
	value: any;
	highlight: boolean;
}

export interface RelatedEntity {
	id: number;
	name: string;
}

export interface RequestLimits {
	periodType: 'hour' | 'day' | 'week' | '';
    periodNumber: number | null;
    limit: number | null;
    effectiveFrom: string;
	requestLimitsCheckbox?: boolean;
}

export interface Client<ID = Id> {
	id: ID;
	login: string | null;
	name: string;
	password: string;
	productId: string;
	availableInterfaces: HlrInterface[];
	removeLeadingZeroFromMnc: boolean;
	maxSessionsAmount: number;
	enumAddressList: string[];
	enumLoginAuth: boolean;
	fieldsOfResponse: string[];
	allowDebug: boolean | null;
	forceResultToOne: boolean | null;
	requestLimits: RequestLimits | null;
	enableOAuth?: boolean;
	oauthScopes?: string[];
}

export interface ResponseFields {
	enumName: string;
	nameV1: string;
	nameV2: string;
	isVisibility: boolean;
	isDefaultSelected: boolean;
}

export interface HlrRates<ID = Id> {
	id: ID;
	currencyId: number | null;
	currencyName?: string;
	startDate: string | null;
	rate: number | null;
}

export interface HlrCurrencies<ID = Id> {
	id: ID;
	name: string;
	charCode: string;
	currencySymbol: string;
	isSystemCurrency: false;
	currencyIconName: string;
}

export interface HlrSettings<ID = Id> {
	settingId: ID;
	cacheStorageLimit: boolean | null;
	enableGlobalProducts: boolean | null;
	enableGlobalSenders: boolean | null;
	apiHost: string | null;
	defaultConnectionTimeout: number;
	defaultRequestTimeout: number;
	concurrentRequestTimeout: number;
	dnisLimit: number;
}

export interface HistoryLog<ID = Id> {
	ID: ID;
	ActionID: string;
	TableName: string;
	ActionType: string;
	RecordCount: false;
	ActionTime: string;
	EntityNew?: any;
	EntityOld?: any;
	UserID: number;
}

export interface ImportRate<ID = Id> {
	ID: ID;
	Provider: string;
	MCCMNC: string;
	Rate: number;
}

export type ProductsType = 'sms' | 'hlr' | 'all';

export interface Product {
	product_id: number;
	is_active?: number;
	product_caption: string;
	acc_currency_code?: string;
	product_direction?: number;
	product_type?: number;
}

export interface Provider {
	name: string;
	description: string;
	groups: {
		name: string;
		fields: ProviderField[];
	}[];
	type: string
}

export interface ProviderField {
	fieldName: string;
	cfgFieldName: string;
	webDescription: string;
	mandatory: boolean;
}

export interface CamaraRule {
	id: number | null;
	ruleType: string | null;
	prefixList: string[];
	clientList: number[];
	source: string;
	author?: Author | null;
	createdBy?: string;
	lastUpdated?: string;
}

export const OAUTH_TYPES: {
	name: string;
	value: string;
} [] = [
	{ name: 'Sim swap (check)', value: 'SIM_SWAP_CHECK' },
	{ name: 'Sim swap (retrieve date)', value: 'SIM_SWAP_RETRIEVE_DATE' },
	{ name: 'Number verification (verify)', value: 'NUMBER_VERIFICATION_VERIFY' },
	{ name: 'Number verification (phone number)', value: 'NUMBER_VERIFICATION_DEVICE_PHONE_NUMBER' },
	{ name: 'Know your customer (data match)', value: 'KYC_MATCH_MATCH' },
];

export interface Rule<ID = Id> {
	ruleId: ID;
	name: string;
	productList: string[] | null;
	prefixList: string[];
	senderList: string[] | null;
	choiceList: ChoiceHLR[] | ChoiceLocal[];
	choiceListName?: string[];
	productsType: string;
	productsNames: string[] | null;
}

export interface ChoiceHLR {
	sources: Record<string, number>[];
	enableLCR: boolean;
}

export interface ChoiceLocal {
	sources: Array<{
		Source: string[];
		Share: number;
	}>;
	enableLCR: string;
}

export type HuntStop = 'DISABLED' | 'ON_TIMEOUT' | 'ON_FAILED_RESPONSE' | 'UNCONDITIONAL';

export interface Source<ID = Id> {
	[key: string]: unknown;
	SourceId: ID;
	CurrencyName: string | null;
	Name: string;
	Type: string;
	ConnectionType: string;
	Ttl: number | null;
	Url: string;
	StatusRequestUrl: string;
	HuntStop: HuntStop | null;
	FieldsAll: boolean | null;
	Username: string;
	Password: string;
	RequestTimeout: number | null;
	Email: string;
	CustomerId: string;
	SourceGt: string;
	DestinationGt: string;
	ServiceCentreAddress: string;
	VerifiedSmsAgentId: string;
	VerifiedSmsServiceAccountPath: string;
	VerifiedSmsPrivateKeyPath: string;
	Domain: string;
	Ip: string;
	Port: number | null;
	CustomTranslation: string | null;
	MccmncTranslation: string | null;
	MccmncTranslationBy: string | null;
	MccmncTranslationBy2: string | null;
	MccSet: string | null;
	ResponseCodeCacheList: string | null;
	ErrorCodeTranslation: string | null;
	ApiKey: string;
	CustomerKey: string;
	SecretKey: string;
	ForceErrCodeTranslation: boolean | null;
	Retries: number | null;
	ForceRerouteKeepingMccmnc: boolean | null;
	CacheSuccessfulResponseOnly: number | null;
	UseErrorCode: boolean | null;
	UseStatusId: boolean | null;
	FixedMccMnc: boolean | null;
	InvNumberCode: number | null;
	HlrResponseCodeFrom: string;
	PresenceMccmnc: string;
	SenderAddress: string;
	SenderPort: number | null;
	IgnoreNotPorted: boolean | null;
	IgnoreWireline: boolean | null;
	StatusRequestDelay: number | null;
	ActiveDb: number | null;
	StageDb: number | null;
	DoNotUseMccmnc: boolean | null;
	MnpFieldType: string;
}

export interface MDTask<ID = Id> {
	mdTaskID: ID;
	status: 'completed' | 'in_progress' | 'failed' | null;
	createdAt: string;
	author: Author | null;
	sources: string[];
	numbers: string[];
	noCache: boolean;
	totalAmount: number;
	successfulAmount: number;
	mccmncAmount: number;
	portedAmount: number;
	notPortedAmount: number;
	emptyAmount: number;
	cachedAmount: number;
	errorsAmount: number;
	mccmnc: number | null;
}

export interface MDTaskResult {
	mdTaskID: number;
	number: string;
	mccmnc: string;
	ported: number;
	source: string;
	sourceName: string;
	sourceType: string;
	responseTime: number;
	contextLog: string;
	result: number;
	errorMessage: string;
	cached: number;
}

export interface MDTaskCreateDTO {
	Sources: string[];
	NoCache: boolean;
	Numbers: string[];
}

export interface Simulation<ID = Id> {
	id: ID;
	status: 'completed' | 'inProgress' | 'failed' | null;
	createdAt: string;
	dippingRule: string;
	author: Author | null;
	senderId: string;
	prefix: string;
	productId: number;
	productName: string;
	productLabel?: string;
}

export interface SiTask {
	productId: number | null;
	productLabel?: string | null;
	productName: string | null;
	senderId: string | null;
	prefix: string | null;
}

export interface SystemCode<ID = Id> {
	id: ID;
	value: string | null;
	type?: string;
	description: string | null
	lastUpdated?: string;
}

export interface TranslationRule<ID = Id> {
	id: ID;
	name: string | null;
	status: boolean | null;
	sourceType: string | null;
	responseCodes: string | null;
	systemCode?: string | null;
	systemCodeId?: number | null;
	lastUpdated?: string | null;
}

/* End of HLR */
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
